import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { CeleryTaskStatusResponse } from '@/api/generalApi'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { TestMilestoneConnectionParams } from '@/api/vlmApi'
import { useWaitForCeleryTaskResultsAsync } from '@/hooks/useWaitForCeleryTaskResultsAsync'
import { useTestMilestoneConnection } from '@/queries/vlmQueries'

type MilestoneCredentials = Omit<TestMilestoneConnectionParams, 'deviceId'>

export type ConnectionTestResult = {
  status: 'success' | 'error'
  message: string
}

type UseMilestoneConnectionTestParams = {
  deviceId: string
}

export const useMilestoneConnectionTest = ({
  deviceId
}: UseMilestoneConnectionTestParams) => {
  const intl = useIntl()
  const [result, setResult] = useState<ConnectionTestResult | null>(null)

  const { mutateAsync: testConnection, isPending: isDispatching } =
    useTestMilestoneConnection()

  const { waitForCeleryTaskResultsAsync, isPending: isPolling } =
    useWaitForCeleryTaskResultsAsync()

  const resetResult = useCallback(() => setResult(null), [])

  const handleTestConnection = useCallback(
    async (credentials: MilestoneCredentials) => {
      if (!deviceId) {
        return
      }

      setResult(null)

      try {
        const { task_id } = await testConnection({ deviceId, ...credentials })
        await waitForCeleryTaskResultsAsync(task_id)

        setResult({
          status: 'success',
          message: intl.formatMessage({
            id: 'vlm.milestoneServers.testConnection.success'
          })
        })
      } catch (err) {
        // A failed Celery task rejects with the task response, not an Axios error.
        const taskResult = (err as CeleryTaskStatusResponse | undefined)
          ?.task_result

        const fallback = intl.formatMessage({
          id: 'vlm.milestoneServers.testConnection.error'
        })

        setResult({
          status: 'error',
          message:
            typeof taskResult === 'string'
              ? taskResult
              : extractErrorMessage(err, fallback)
        })
      }
    },
    [deviceId, testConnection, waitForCeleryTaskResultsAsync, intl]
  )

  return useMemo(
    () => ({
      isTesting: isDispatching || isPolling,
      result,
      resetResult,
      testConnection: handleTestConnection
    }),
    [isDispatching, isPolling, result, resetResult, handleTestConnection]
  )
}
